import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { CircularProgress, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import useStyles from "./FormShell.styles";
import { SectionStatusShape } from "./formShell.propTypes";
import {
  SECTION_STATE,
  getSectionState,
  summariseSections,
} from "./formShell.status";

const issuesText = (n) => `${n} ${n === 1 ? "issue" : "issues"}`;

/** Progress ring plus one quick-nav button per section (markup from Add_Loan). */
function FormProgressHeader({
  sections,
  onSectionClick,
  progressLabel = "Form progress",
  completeLabel = "Ready to save",
  extraErrors = 0,
  optionalCountsAsComplete = true,
  sticky = false,
  className,
}) {
  const classes = useStyles();
  const summary = summariseSections(sections);
  const errors = summary.errors + extraErrors;

  return (
    <div
      className={clsx(
        classes.progressHeader,
        sticky && classes.progressHeaderSticky,
        className
      )}
    >
      <div className={classes.progressRingWrap}>
        <div className={classes.progressRing}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={40}
            thickness={4}
            className={classes.progressRingTrack}
          />
          <CircularProgress
            variant="determinate"
            value={summary.percent}
            size={40}
            thickness={4}
            className={classes.progressRingValue}
          />
          <div className={classes.progressRingLabel}>
            <span className={classes.progressRingPercent}>
              {summary.percent}%
            </span>
          </div>
        </div>
        <div className={classes.progressMetaText}>
          <Typography className={classes.progressTitle}>
            {summary.percent === 100 ? completeLabel : progressLabel}
          </Typography>
          <Typography className={classes.progressSubtitle}>
            {summary.filled}/{summary.total} required
            {errors > 0 ? ` · ${errors} to fix` : ""}
          </Typography>
        </div>
      </div>
      <div className={classes.quickNav}>
        {sections.map((section) => {
          // The quick-nav ignores `locked`, as Add_Loan's always has (LLD §3.4).
          const state = getSectionState(
            { ...section, locked: false },
            { optionalCountsAsComplete }
          );
          const isError = state === SECTION_STATE.ERROR;
          const isComplete = state === SECTION_STATE.COMPLETE;
          const stateText = isError
            ? issuesText(section.errorCount)
            : isComplete
            ? "complete"
            : "in progress";
          return (
            <button
              type="button"
              key={section.id}
              className={clsx(
                classes.quickNavItem,
                isError && classes.quickNavError,
                isComplete && classes.quickNavComplete
              )}
              onClick={() => onSectionClick(section.id)}
              title={section.title}
              aria-label={`${section.title}: ${stateText}`}
            >
              <span className={classes.quickNavIcon}>
                {isError ? (
                  <ErrorOutlineIcon style={{ fontSize: "0.9375rem" }} />
                ) : isComplete ? (
                  <CheckCircleIcon style={{ fontSize: "0.9375rem" }} />
                ) : section.icon ? (
                  React.cloneElement(section.icon, {
                    style: { fontSize: 15 },
                  })
                ) : null}
              </span>
              {section.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

FormProgressHeader.propTypes = {
  sections: PropTypes.arrayOf(SectionStatusShape).isRequired,
  onSectionClick: PropTypes.func.isRequired,
  progressLabel: PropTypes.string,
  completeLabel: PropTypes.string,
  extraErrors: PropTypes.number,
  optionalCountsAsComplete: PropTypes.bool,
  sticky: PropTypes.bool,
  // Host margins only — never position; use `sticky` for that (LLD §3.7).
  className: PropTypes.string,
};

export default React.memo(FormProgressHeader);
